// Routing the AI terminal's calls to the user's own machine (TODO §14.5.1).
//
// The terminal ([[ai-terminal-localhost-constraint]]) talks to a model runner on
// `http://localhost:12434`. No browser origin may call that — Docker Model Runner sends no
// CORS headers, and Private Network Access blocks it besides — so in the desktop shell the
// call is made by the main process instead, which is Node and has no CORS to answer to.
//
// This is the page-side half: a `window.fetch` wrapper that diverts exactly those URLs to the
// shell bridge and leaves every other request alone. It lives in the web bundle rather than
// in the preload because with context isolation on (§14.4.3) the preload's `window` is not
// the page's — patching it there would shim nothing.
//
// Two things it deliberately does NOT do:
//   • fork `packages/react-terminal` (§14.5.5) — the terminal keeps calling plain `fetch`,
//     and stays a generic library that works in any host;
//   • buffer the response. The terminal streams tokens over SSE, so the shim rebuilds a real
//     streaming `Response` from the bridge's chunk callbacks. Anything else would turn a live
//     stream into one late blob.

import { desktopShell } from "@/lib/desktopBridge";

// The wrapper is installed once per page. A second install would double-wrap `fetch` and
// route every request through two shims.
let installed = false;

type FetchArgs = Parameters<typeof fetch>;

interface NormalizedRequest {
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string | Uint8Array | null;
}

function headersToRecord(
  init: HeadersInit | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!init) return out;
  new Headers(init).forEach((value, key) => {
    out[key] = value;
  });
  return out;
}

/**
 * Flatten whatever `fetch` was called with into something that can cross `contextBridge`.
 * Returns null for a call this shim cannot faithfully forward (a streamed request body, a
 * `FormData`), which then goes down the untouched original path rather than being mangled.
 */
async function normalize(
  input: FetchArgs[0],
  init: FetchArgs[1],
): Promise<NormalizedRequest | null> {
  const isRequest = typeof Request !== "undefined" && input instanceof Request;
  const rawUrl = isRequest ? input.url : String(input);
  let url: string;
  try {
    url = new URL(rawUrl, window.location.href).toString();
  } catch {
    return null;
  }

  const method = (
    init?.method ?? (isRequest ? input.method : "GET")
  ).toUpperCase();
  const headers = {
    ...(isRequest ? headersToRecord(input.headers) : {}),
    ...headersToRecord(init?.headers),
  };

  let body: string | Uint8Array | null = null;
  const rawBody = init?.body ?? null;
  if (rawBody !== null) {
    if (typeof rawBody === "string") body = rawBody;
    else if (rawBody instanceof Uint8Array) body = rawBody;
    else if (rawBody instanceof ArrayBuffer) body = new Uint8Array(rawBody);
    else if (ArrayBuffer.isView(rawBody))
      body = new Uint8Array(
        rawBody.buffer,
        rawBody.byteOffset,
        rawBody.byteLength,
      );
    else return null; // FormData, Blob, ReadableStream — not something to guess at.
  } else if (isRequest && input.body) {
    body = new Uint8Array(await input.clone().arrayBuffer());
  }

  return { url, method, headers, body };
}

function bridgeFetch(
  req: NormalizedRequest,
  signal: AbortSignal | null | undefined,
): Promise<Response> {
  const shell = desktopShell();
  if (!shell) return Promise.reject(new Error("desktop shell unavailable"));

  return new Promise<Response>((resolve, reject) => {
    let abort: (() => void) | null = null;
    let controller: ReadableStreamDefaultController<Uint8Array> | null = null;
    // Chunks can arrive before the consumer has pulled the stream's `start`, and a token
    // dropped on the floor is a corrupted answer — so they queue until the controller exists.
    const pending: Uint8Array[] = [];
    let ended = false;
    let failed: Error | null = null;

    const stream = new ReadableStream<Uint8Array>({
      start(c) {
        controller = c;
        for (const chunk of pending) c.enqueue(chunk);
        pending.length = 0;
        if (failed) c.error(failed);
        else if (ended) c.close();
      },
      cancel() {
        // The consumer walked away (the terminal's Stop button, or an unmount).
        abort?.();
      },
    });

    abort = shell.startLocalModelRequest(req, {
      onHead: (head) => {
        // 204/304 must not carry a body, and constructing one throws.
        const bodyless =
          head.status === 204 || head.status === 304 || head.status === 205;
        resolve(
          new Response(bodyless ? null : stream, {
            status: head.status,
            statusText: head.statusText,
            headers: head.headers,
          }),
        );
      },
      onChunk: (chunk) => {
        if (controller) controller.enqueue(chunk);
        else pending.push(chunk);
      },
      onEnd: () => {
        ended = true;
        try {
          controller?.close();
        } catch {
          // Already closed or errored — the consumer has what it needs either way.
        }
      },
      onError: (message) => {
        const error = new Error(message);
        failed = error;
        if (controller) controller.error(error);
        // Before the head arrives there is no Response yet, so the failure surfaces as a
        // rejected `fetch` — which is exactly how a network error reads on the web.
        reject(error);
      },
    });

    if (signal) {
      if (signal.aborted) abort();
      else signal.addEventListener("abort", () => abort?.(), { once: true });
    }
  });
}

/**
 * Install the shim. Safe to call anywhere, in any runtime: on the website there is no shell
 * bridge and this is a no-op, so `main.tsx` calls it unconditionally.
 */
export function installLocalModelFetch(): void {
  const shell = desktopShell();
  if (!shell || installed || typeof window === "undefined") return;
  installed = true;

  const original = window.fetch.bind(window);
  window.fetch = async (
    input: FetchArgs[0],
    init?: FetchArgs[1],
  ): Promise<Response> => {
    // Cheap check first: the overwhelming majority of calls are our own assets and the
    // premium API, and none of them should pay for normalization.
    const quickUrl =
      typeof input === "string"
        ? input
        : typeof Request !== "undefined" && input instanceof Request
          ? input.url
          : String(input);
    let absolute: string;
    try {
      absolute = new URL(quickUrl, window.location.href).toString();
    } catch {
      return original(input, init);
    }
    if (!shell.isLocalModelUrl(absolute)) return original(input, init);

    const req = await normalize(input, init);
    if (!req) return original(input, init);
    return bridgeFetch(req, init?.signal ?? null);
  };
}
